import type { ApiClient } from '../client';

const BASE = '/loot_machine/api/game_platform/iframe/v1/addresses';

/** Shipping address record returned by the addresses API. */
export interface ShippingAddress {
  id: number;
  name: string;
  phoneNumber: string;
  firstLine: string;
  secondLine: string | null;
  city: string;
  state: string | null;
  country: string;
  zipcode: string;
  additional: string | null;
  isDefault: boolean;
}

/** Shared body for create + update. The backend mirrors these names via
 *  Rails strong-params (`first_line`, `phone_number`, …) under an
 *  `address` root. Optional fields are sent as empty strings (never
 *  omitted) because `city`/`state`/`zipcode` are `null: false` columns. */
export interface AddressInput {
  name: string;
  phoneNumber: string;
  firstLine: string;
  secondLine?: string;
  city: string;
  state?: string;
  country: string;
  zipcode: string;
  additional?: string;
  isDefault?: boolean;
  /** Vestigial web flag (the backend default-sets on its own). Accepted
   *  for call-site parity; not sent in the request body. */
  confirm?: boolean;
}

export type CreateAddressParams = AddressInput;
export type UpdateAddressParams = AddressInput;

export interface RawAddress {
  id?: number | string;
  name?: string;
  phone_number?: string;
  first_line?: string;
  second_line?: string | null;
  city?: string;
  state?: string | null;
  country?: string;
  zipcode?: string;
  additional?: string | null;
  is_default?: boolean;
}

export function mapAddress(raw: RawAddress): ShippingAddress {
  return {
    id: Number(raw.id ?? 0),
    name: raw.name ?? '',
    phoneNumber: raw.phone_number ?? '',
    firstLine: raw.first_line ?? '',
    secondLine: raw.second_line ?? null,
    city: raw.city ?? '',
    state: raw.state ?? null,
    country: raw.country ?? '',
    zipcode: raw.zipcode ?? '',
    additional: raw.additional ?? null,
    isDefault: Boolean(raw.is_default),
  };
}

/** Wrap under the `address` root that the backend's
 *  `params.require(:address)` expects, with empty-string defaults for the
 *  nullable-but-NOT-NULL columns. */
function toBody(params: AddressInput) {
  return {
    address: {
      name: params.name,
      phone_number: params.phoneNumber,
      first_line: params.firstLine,
      second_line: params.secondLine ?? '',
      city: params.city,
      state: params.state ?? '',
      country: params.country,
      zipcode: params.zipcode,
      additional: params.additional ?? '',
      ...(params.isDefault !== undefined ? { is_default: params.isDefault } : {}),
    },
  };
}

/** GET /addresses — the player's saved shipping addresses, default first. */
export async function listAddresses(client: ApiClient): Promise<ShippingAddress[]> {
  const raw = await client.request<{ addresses?: RawAddress[] } | undefined>(BASE, {
    method: 'GET',
  });
  return (raw?.addresses ?? []).map(mapAddress);
}

/** A save returned no `address` — almost always means the request didn't
 *  reach the addresses endpoint (not deployed on the target backend, or
 *  pointed at the wrong API URL). Surface a readable message instead of a
 *  cryptic "Cannot read properties of undefined". */
function requireAddress(raw: { address?: RawAddress } | undefined): RawAddress {
  if (!raw?.address) {
    throw new Error(
      'Could not save the address — the addresses endpoint is unavailable on this backend.',
    );
  }
  return raw.address;
}

/** POST /addresses — create + auto-default. */
export async function createAddress(
  client: ApiClient,
  params: CreateAddressParams,
): Promise<ShippingAddress> {
  const raw = await client.request<{ address?: RawAddress } | undefined>(BASE, {
    method: 'POST',
    body: toBody(params),
  });
  return mapAddress(requireAddress(raw));
}

/** PUT /addresses/:id — edit an existing address. */
export async function updateAddress(
  client: ApiClient,
  id: number,
  params: UpdateAddressParams,
): Promise<ShippingAddress> {
  const raw = await client.request<{ address?: RawAddress } | undefined>(`${BASE}/${id}`, {
    method: 'PUT',
    body: toBody(params),
  });
  return mapAddress(requireAddress(raw));
}

/** PUT /addresses/:id/default — make this the player's default shipping
 *  address (the backend unsets the previous default). */
export async function setDefaultAddress(
  client: ApiClient,
  id: number,
): Promise<ShippingAddress> {
  const raw = await client.request<{ address?: RawAddress } | undefined>(
    `${BASE}/${id}/default`,
    { method: 'PUT' },
  );
  return mapAddress(requireAddress(raw));
}
